import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

interface ModalProps {
  titleId: string;
  descriptionId: string;
  maxWidth: string;
  /** `alertdialog` for a question that needs an answer; `dialog` for a form. */
  role?: 'alertdialog' | 'dialog';
  /** Top padding class: the viewer sits higher than a question dialog. */
  topPadding?: string;
  /** While a request runs the dialog cannot be dismissed. */
  locked: boolean;
  onClose: () => void;
  /** Receives the focus when the dialog opens: the least destructive choice, or the first field. */
  initialFocus: RefObject<HTMLElement | null>;
  children: ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled])';

/** Modal dialog of the inbox: focus trap, Esc to close (unless locked), dimmed overlay. */
export const Modal = ({
  titleId,
  descriptionId,
  maxWidth,
  role = 'alertdialog',
  topPadding = 'pt-[min(220px,20vh)]',
  locked,
  onClose,
  initialFocus,
  children,
}: ModalProps) => {
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initialFocus.current?.focus();
  }, [initialFocus]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!locked) onClose();
        return;
      }
      if (event.key !== 'Tab' || !boxRef.current) return;
      const focusable = Array.from(boxRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !boxRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !boxRef.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [locked, onClose]);

  return (
    <div className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-stone-900/45 px-4 pb-6 ${topPadding}`}>
      <div
        ref={boxRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={`flex w-full flex-col gap-3.5 rounded-[20px] bg-white p-7 shadow-[0_20px_50px_rgba(28,25,23,0.25)] ${maxWidth}`}
      >
        {children}
      </div>
    </div>
  );
};
